// Tickets — list + detail (kit .two-column), not a table of inline selects.
// Filing goes through one modal with the "où dans le processus ?" element
// picker fed by the BPMN index. Two terminal outcomes: resolve (note required)
// and open a rework.
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import { useSearch } from '../context/SearchContext';
import { useT } from '../i18n';
import { agoLabel, formatDate } from '../utils/format';

type Filter = 'triage' | 'progress' | 'mine' | 'resolved' | 'all';

export function TicketsPage() {
  const { t, lang } = useT();
  const { isCde, isGovernance, isAdmin } = useRole();
  const canTriage = isCde || isGovernance || isAdmin;
  const { searchTerm } = useSearch();
  const [tickets, setTickets] = useState<any[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [filing, setFiling] = useState(false);
  const [comment, setComment] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolution, setResolution] = useState('');

  const load = () => {
    const params: Record<string, string> = {};
    if (filter === 'triage') params.status = 'open';
    if (filter === 'progress') params.status = 'in_progress';
    if (filter === 'mine') params.mine = '1';
    if (filter === 'resolved') params.status = 'resolved';
    return api.getTickets(params).then(setTickets);
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    api.getTicket(selectedId).then(setDetail).catch(() => {});
  }, [selectedId]);

  const visible = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q || !tickets) return tickets ?? [];
    return tickets.filter(tk => tk.title.toLowerCase().includes(q) || (tk.process_name ?? '').toLowerCase().includes(q));
  }, [tickets, searchTerm]);

  const postComment = async () => {
    if (!comment.trim() || !detail) return;
    await api.commentTicket(detail.id, comment.trim());
    setComment('');
    api.getTicket(detail.id).then(setDetail);
  };

  const resolve = async () => {
    if (!resolution.trim() || !detail) return;
    await api.updateTicket(detail.id, { status: 'resolved', resolution: resolution.trim() });
    setResolving(false); setResolution('');
    api.getTicket(detail.id).then(setDetail);
    load();
  };

  const spawn = async () => {
    if (!detail) return;
    const created = await api.spawnRework(detail.id);
    window.location.hash = `#/process/${created.id}`;
  };

  const chips: [Filter, string][] = [
    ['all', t('common.all')], ['triage', t('tk.filter.toTriage')], ['progress', t('tk.filter.inProgress')],
    ['mine', t('tk.filter.mine')], ['resolved', t('tk.filter.resolved')],
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('shell.title.tickets')}</h1>
          <p className="page-meta">{visible.length}</p>
        </div>
        <button className="btn btn-accent" onClick={() => setFiling(true)}><Plus className="icon-sm" /> {t('tk.new')}</button>
      </div>

      <div className="toolbar">
        <div className="filter-chips">
          {chips.map(([k, label]) => (
            <button key={k} className={`chip ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>{label}</button>
          ))}
        </div>
      </div>

      {tickets && visible.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-desc">{t('tk.empty')}</div>
          <button className="btn btn-accent mt-16" onClick={() => setFiling(true)}><Plus className="icon-sm" /> {t('tk.new')}</button>
        </div>
      ) : (
        <div className="two-column">
          <div className="flex flex-col gap-8">
            {(visible ?? []).map(tk => (
              <div key={tk.id}
                   className={`card card-clickable${selectedId === tk.id ? ' selected' : ''}`}
                   onClick={() => setSelectedId(tk.id)}>
                <div className="p-12">
                  <div className="flex items-center gap-8">
                    {tk.severity === 'high' && <span className="badge badge-err">!</span>}
                    <span className="font-medium text-ink-1" style={{ flex: 1 }}>{tk.title}</span>
                    <span className="badge">{t(`tk.status.${tk.status}`)}</span>
                  </div>
                  <div className="text-xs text-ink-3" style={{ marginTop: 2 }}>
                    {tk.process_code ? `${tk.process_code} · ${tk.process_name} · ` : ''}
                    {t('tk.by', { name: tk.submitted_by_name ?? '—' })} · {agoLabel(tk.created_at, lang)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="panel">
            {!detail ? (
              <div className="panel-body text-ink-3 text-sm">{t('tk.select')}</div>
            ) : (
              <>
                <div className="panel-header">
                  <div>
                    <div className="font-medium text-ink-1">{detail.title}</div>
                    <div className="text-xs text-ink-3">
                      {detail.process_name
                        ? <a className="text-accent" href={`#/process/${detail.process_id}`}>{detail.process_name}</a>
                        : t('tk.unlinked')}
                      {' · '}{t('tk.by', { name: detail.submitted_by_name ?? '—' })} · {formatDate(detail.created_at, lang)}
                    </div>
                  </div>
                </div>
                <div className="panel-body">
                  {detail.bpmn_element_name && (
                    <div className="detail-row mb-8">
                      <span className="badge badge-plain">{t('tk.elementAt', { el: detail.bpmn_element_name })}</span>
                    </div>
                  )}
                  {detail.body && <div className="detail-text mb-16">{detail.body}</div>}

                  {detail.spawned_process_id && (
                    <div className="mb-16">
                      <a className="text-accent text-sm" href={`#/process/${detail.spawned_process_id}`}>
                        {t('tk.spawnedShort', { code: `cde${detail.spawned_process_id}` })}
                      </a>
                    </div>
                  )}
                  {detail.resolution && (
                    <div className="detail-section">
                      <div className="detail-label">{t('tk.resolve')}</div>
                      <div className="detail-text">{detail.resolution}</div>
                    </div>
                  )}

                  {canTriage && detail.status !== 'resolved' && detail.status !== 'rejected' && (
                    <div className="flex gap-8 items-center mb-16" style={{ flexWrap: 'wrap' }}>
                      <div className="selectable-group">
                        {['open', 'triage', 'in_progress'].map(s => (
                          <button key={s} type="button" className={`selectable${detail.status === s ? ' active' : ''}`}
                                  onClick={() => api.updateTicket(detail.id, { status: s }).then(() => { api.getTicket(detail.id).then(setDetail); load(); })}>
                            {t(`tk.status.${s}`)}
                          </button>
                        ))}
                      </div>
                      <span style={{ marginLeft: 'auto' }} />
                      {isCde && detail.process_id && !detail.spawned_process_id && (
                        <button className="btn btn-ghost btn-sm" onClick={spawn}>{t('tk.openRework')}</button>
                      )}
                      <button className="btn btn-accent btn-sm" onClick={() => setResolving(r => !r)}>{t('tk.resolve')}</button>
                    </div>
                  )}
                  {resolving && (
                    <div className="mb-16">
                      <div className="form-group">
                        <label className="form-label">{t('tk.resolution')}</label>
                        <textarea className="form-textarea" rows={2} autoFocus value={resolution} onChange={e => setResolution(e.target.value)} />
                      </div>
                      <button className="btn btn-accent btn-sm mt-8" disabled={!resolution.trim()} onClick={resolve}>{t('common.confirm')}</button>
                    </div>
                  )}

                  <div className="detail-section">
                    <div className="detail-label">{t('tk.comment')}</div>
                    {(detail.comments ?? []).map((c: any) => (
                      <div key={c.id} className="detail-row" style={{ display: 'block', marginBottom: 'var(--s8)' }}>
                        <span className="text-xs text-ink-3">{c.author_name} · {formatDate(c.created_at, lang)}</span>
                        <div className="text-sm">{c.body}</div>
                      </div>
                    ))}
                    <div className="flex gap-8 items-end">
                      <input className="form-input--bare" style={{ flex: 1 }} placeholder={t('tk.comment')} value={comment}
                             onChange={e => setComment(e.target.value)}
                             onKeyDown={e => { if (e.key === 'Enter') postComment(); }} />
                      <button className="btn btn-sm" disabled={!comment.trim()} onClick={postComment}>{t('common.send')}</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {filing && <FileTicketModal onClose={() => setFiling(false)} onFiled={() => { setFiling(false); load(); }} />}
    </>
  );
}

export function FileTicketModal({ presetProcessId, onClose, onFiled }: {
  presetProcessId?: number;
  onClose: () => void;
  onFiled: () => void;
}) {
  const { t } = useT();
  const [processes, setProcesses] = useState<any[]>([]);
  const [index, setIndex] = useState<any | null>(null);
  // the picker value is "cycle:<id>" for a lifecycle record or "file:<path>" for
  // a documented file, so a ticket can target either of the 90 processes.
  const [target, setTarget] = useState(presetProcessId ? `cycle:${presetProcessId}` : '');
  const [form, setForm] = useState({ bpmn_element_name: '', title: '', body: '', severity: 'normal' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getProcesses().then(setProcesses).catch(() => {});
    api.getRepoIndex().then(setIndex).catch(() => {});
  }, []);

  // combined target list: cycle records first, then documented files with no cycle
  const linkedPaths = useMemo(() => new Set(processes.map(p => p.git_path).filter(Boolean)), [processes]);
  const documented = useMemo(() =>
    (index?.entries ?? []).filter((e: any) => !e.isEmpty && !e.isTechnical && !linkedPaths.has(e.path)),
    [index, linkedPaths]);

  // «où dans le processus ?» — lanes + named gateways/events/tasks of the diagram
  const elements = useMemo(() => {
    let path: string | undefined;
    if (target.startsWith('cycle:')) path = processes.find(p => String(p.id) === target.slice(6))?.git_path;
    else if (target.startsWith('file:')) path = target.slice(5);
    const entry = index?.entries?.find((e: any) => e.path === path);
    if (!entry) return [];
    return [
      ...(entry.lanes ?? []).map((l: string) => ({ group: 'lane', name: l })),
      ...(entry.elements ?? []).map((el: any) => ({ group: el.type.includes('Gateway') || el.type.includes('Event') ? 'gw' : 'task', name: el.name })),
    ];
  }, [processes, index, target]);

  const submit = async () => {
    if (!form.title.trim()) return;
    try {
      await api.createTicket({
        process_id: target.startsWith('cycle:') ? Number(target.slice(6)) : null,
        git_path: target.startsWith('file:') ? target.slice(5) : null,
        bpmn_element_name: form.bpmn_element_name || null,
        title: form.title, body: form.body || null, severity: form.severity,
      });
      onFiled();
    } catch (e: any) { setError(e.message); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{t('tk.new')}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group mb-12">
            <label className="form-label">{t('tk.process')}</label>
            <select className="form-select" value={target}
                    onChange={e => { setTarget(e.target.value); setForm({ ...form, bpmn_element_name: '' }); }}>
              <option value="">—</option>
              {processes.length > 0 && (
                <optgroup label={t('tk.grpCycle')}>
                  {processes.map(p => <option key={p.id} value={`cycle:${p.id}`}>{p.process_code} — {p.name}</option>)}
                </optgroup>
              )}
              {documented.length > 0 && (
                <optgroup label={t('tk.grpDocumented')}>
                  {documented.map((e: any) => <option key={e.path} value={`file:${e.path}`}>{e.title}{e.variant ? ` (${e.variant})` : ''}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          {elements.length > 0 && (
            <div className="form-group mb-12">
              <label className="form-label">{t('tk.where')} <span className="text-ink-3">({t('tk.whereHint')})</span></label>
              <select className="form-select" value={form.bpmn_element_name}
                      onChange={e => setForm({ ...form, bpmn_element_name: e.target.value })}>
                <option value="">—</option>
                {elements.map((el, i) => <option key={i} value={el.name}>{el.group === 'lane' ? '⬛' : '·'} {el.name}</option>)}
              </select>
            </div>
          )}
          <div className="form-group mb-12">
            <label className="form-label">{t('tk.what')}</label>
            <input className="form-input" autoFocus value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="form-group mb-12">
            <label className="form-label">{t('tk.details')}</label>
            <textarea className="form-textarea" rows={3} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('tk.severity')}</label>
            <div className="selectable-group">
              {['low', 'normal', 'high'].map(s => (
                <button key={s} type="button" className={`selectable${form.severity === s ? ' active' : ''}`}
                        onClick={() => setForm({ ...form, severity: s })}>
                  {t(`tk.severity.${s}`)}
                </button>
              ))}
            </div>
          </div>
          {error && <div className="text-err text-sm mt-12">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-accent" disabled={!form.title.trim()} onClick={submit}>{t('common.send')}</button>
        </div>
      </div>
    </div>
  );
}
